import { store } from "../data/store";
import type { BuyerRow } from "../data/types";
import { activeBuyers, unreadTotal } from "./format";

/**
 * New-buyer notifications (FEST-PHASE-2.V).
 *
 * Wired once at app mount via initNotifications(). Subscribes to the
 * store and detects new inbound messages by diffing the buyer list
 * snapshot. For each new inbound, fires (in priority order):
 *
 *   1. Audio chime (always, unless muted)
 *   2. Mobile vibration (PWA only, unless muted)
 *   3. OS native banner (only if Notification permission granted, unless muted)
 *   4. Tab title badge — `(N) Festival Ops` (always, even when muted)
 *   5. Favicon red dot (always, even when muted)
 *
 * Per-buyer dedupe: 5 messages from the same buyer in 30s → 1 ping.
 * First-load suppression: existing unread count when the dashboard
 * opens does NOT trigger pings — only deltas after we're connected.
 */

const SOUND_PATH        = "/notify.mp3";
const DEDUPE_WINDOW_MS  = 30_000;

interface BuyerSnapshot { unread: number; activity: number }
let prevSnapshot: Map<string, BuyerSnapshot> | null = null;
let firstLoadCaptured = false;
const lastNotifyAt = new Map<string, number>();

let audio: HTMLAudioElement | null = null;

export async function requestPermission(): Promise<void> {
  if (typeof Notification === "undefined") return;
  try {
    const perm = await Notification.requestPermission();
    store.setNotificationPermission(perm);
    if (perm === "granted") {
      // Fire-and-forget: register the SW + subscribe so the next
      // inbound message reaches us even when the PWA is closed.
      void ensurePushSubscription();
    }
  } catch {
    // iOS sometimes rejects without a clear error
    store.setNotificationPermission(detectPerm());
  }
}

function detectPerm(): "default" | "granted" | "denied" | "unsupported" {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

/* ============== Web Push (FEST-PHASE-2.W) ============== */

async function ensurePushSubscription(): Promise<void> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    // Wait for activation so pushManager is usable.
    await navigator.serviceWorker.ready;
    // FEST-PHASE-2.W.2 — force-fresh every load. iOS sometimes rotates
    // the underlying push token without notifying us, leaving our
    // stored endpoint dead but the iOS-side subscription object intact.
    // Always drop + recreate so we never push to a stale endpoint.
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      try { await existing.unsubscribe(); } catch { /* ignore */ }
    }
    const vapid = await fetchVapidKey();
    if (!vapid) return;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid).buffer as ArrayBuffer,
    });
    await fetch("/api/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(sub.toJSON()),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("push subscription failed:", e);
  }
}

async function fetchVapidKey(): Promise<string | null> {
  try {
    const r = await fetch("/api/push/vapid-public-key");
    if (!r.ok) return null;
    const { key } = (await r.json()) as { key: string };
    return key;
  } catch { return null; }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - base64.length % 4) % 4);
  const std = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(std);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/* ============== Audio ============== */

function ensureAudio(): void {
  if (audio) return;
  try {
    audio = new Audio(SOUND_PATH);
    audio.preload = "auto";
    audio.volume  = 0.6;
  } catch { audio = null; }
}

function playSound(): void {
  if (!audio) ensureAudio();
  if (!audio) return;
  // Reset to 0 so rapid repeats don't queue.
  try {
    audio.currentTime = 0;
    void audio.play().catch(() => { /* user-gesture not yet given */ });
  } catch { /* ignore */ }
}

/** Browsers require a user gesture before allowing audio. We arm
 *  the audio element on the first interaction with the page so the
 *  next inbound message can chime without the gesture in scope. */
function armAudioOnFirstGesture(): void {
  const arm = () => {
    ensureAudio();
    if (audio) {
      audio.muted = true;
      audio.play().then(() => {
        audio!.pause();
        audio!.currentTime = 0;
        audio!.muted = false;
      }).catch(() => { /* fine */ });
    }
    document.removeEventListener("click",      arm);
    document.removeEventListener("touchstart", arm);
    document.removeEventListener("keydown",    arm);
  };
  document.addEventListener("click",      arm, { once: true, passive: true });
  document.addEventListener("touchstart", arm, { once: true, passive: true });
  document.addEventListener("keydown",    arm, { once: true, passive: true });
}

/* ============== Title + favicon ============== */

function updateTitle(): void {
  const total = unreadTotal(activeBuyers(store.state.buyers));
  const base  = "Festival Ops";
  document.title = total > 0 ? `(${total}) ${base}` : base;
}

function updateFavicon(count: number): void {
  // 32×32 canvas: solid dark glass background, optional red dot.
  // We don't depend on the original favicon image (CORS / async loads).
  const canvas = document.createElement("canvas");
  canvas.width = 32; canvas.height = 32;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  // Background — match the dashboard's dark glass tone.
  ctx.fillStyle = "#0c0e16";
  ctx.beginPath();
  ctx.roundRect(0, 0, 32, 32, 7);
  ctx.fill();
  // "F" mark — quick brand glyph in white.
  ctx.fillStyle = "#f5f5f7";
  ctx.font = "bold 22px -apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText("F", 16, 17);
  // Unread dot — red circle in the corner with white outline.
  if (count > 0) {
    ctx.fillStyle = "#ff453a";
    ctx.beginPath();
    ctx.arc(24, 8, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#0c0e16";
    ctx.lineWidth = 2;
    ctx.stroke();
    if (count <= 9) {
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 10px -apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif";
      ctx.fillText(String(count), 24, 9);
    }
  }
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"][data-fest-favicon]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.dataset["festFavicon"] = "1";
    document.head.appendChild(link);
  }
  link.href = canvas.toDataURL("image/png");
}

/* ============== Vibration (mobile) ============== */

function vibrate(): void {
  if (typeof navigator.vibrate !== "function") return;
  try { navigator.vibrate([100, 50, 100]); } catch { /* */ }
}

/* ============== Wiring ============== */

function fireForBuyer(buyer: BuyerRow): void {
  if (!buyer.conv_id) return;
  const now = Date.now();
  const last = lastNotifyAt.get(buyer.conv_id) ?? 0;
  if (now - last < DEDUPE_WINDOW_MS) return;
  lastNotifyAt.set(buyer.conv_id, now);

  if (!store.state.notificationsMuted) {
    playSound();
    vibrate();
    // Note: OS banner notifications are owned by the service worker
    // (sw.js) since FEST-PHASE-2.W.1 — firing both an in-tab
    // Notification AND a push-driven SW notification produced two
    // banners on iOS. SW path is universal (works in foreground,
    // background, and when PWA is closed) so we let it own that.
  }
}

function onStoreUpdate(): void {
  const current = new Map<string, BuyerSnapshot>();
  for (const b of store.state.buyers) {
    if (!b.conv_id) continue;
    current.set(b.conv_id, {
      unread:   b.conv_unread_count,
      activity: b.conv_last_activity_at ?? 0,
    });
  }

  // First load: cache snapshot, sync title + favicon, fire nothing.
  // The unread count that already exists isn't "new" to the operator.
  if (!firstLoadCaptured) {
    prevSnapshot = current;
    firstLoadCaptured = true;
    updateTitle();
    updateFavicon(unreadTotal(activeBuyers(store.state.buyers)));
    return;
  }

  for (const b of store.state.buyers) {
    if (!b.conv_id) continue;
    const prev = prevSnapshot!.get(b.conv_id);
    const cur  = current.get(b.conv_id)!;
    const isNew = !prev
      ? cur.unread > 0
      : cur.unread > prev.unread && cur.activity > prev.activity;
    if (isNew) fireForBuyer(b);
  }

  prevSnapshot = current;
  updateTitle();
  updateFavicon(unreadTotal(activeBuyers(store.state.buyers)));
}

export function initNotifications(): void {
  armAudioOnFirstGesture();
  store.subscribe(onStoreUpdate);
  // Fire once immediately to set the initial title + favicon even
  // if the store is still empty (it'll re-fire when buyers load).
  onStoreUpdate();
  // Sync the in-store permission mirror in case Notification.permission
  // changed in another tab (rare but harmless to recheck).
  if (typeof Notification !== "undefined") {
    store.setNotificationPermission(Notification.permission);
    // If the operator already granted permission in a previous session,
    // make sure the push subscription is current too. New devices may
    // have lost their subscription; this ensures every load re-syncs.
    if (Notification.permission === "granted") {
      void ensurePushSubscription();
    }
  }
}
